import type { UIMessage } from 'ai';
import {
  RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

const PIN_THRESHOLD = 80;
const TOP_THRESHOLD = 120;

type UseChatScrollOptions = {
  onReachTop?: () => void;
  isLoadingOlder?: boolean;
};

const measureContentLength = (message?: UIMessage): number => {
  if (!message) return 0;
  return message.parts.reduce((acc, part) => {
    if (part.type === 'text' || part.type === 'reasoning') {
      return acc + part.text.length;
    }
    const { data } = part as { data?: unknown };
    if (part.type.startsWith('data-') && typeof data === 'string') {
      return acc + data.length;
    }
    return acc;
  }, 0);
};

type ChatScroll = {
  containerRef: RefObject<HTMLDivElement>;
  isScrolledUp: boolean;
  scrollToLatest: () => void;
};

export const useChatScroll = (
  messages: UIMessage[],
  { onReachTop, isLoadingOlder = false }: UseChatScrollOptions = {},
): ChatScroll => {
  const containerRef = useRef<HTMLDivElement>(null);
  const lastSignatureRef = useRef('');
  const lastCountRef = useRef(0);
  const lastIdsRef = useRef({ first: '', last: '' });
  const lastScrollTopRef = useRef(0);
  const lastScrollHeightRef = useRef(0);
  const isPinnedRef = useRef(true);
  const hasRequestedOlderRef = useRef(false);
  const [isScrolledUp, setIsScrolledUp] = useState(false);
  const hasMessages = messages.length > 0;

  const onReachTopRef = useRef(onReachTop);
  onReachTopRef.current = onReachTop;

  const isLoadingOlderRef = useRef(isLoadingOlder);
  isLoadingOlderRef.current = isLoadingOlder;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const handleScroll = () => {
      // A container without a layout box measures zero, which reads as pinned.
      if (container.clientHeight === 0) return;

      const distanceFromBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight;
      isPinnedRef.current = distanceFromBottom < PIN_THRESHOLD;
      setIsScrolledUp(!isPinnedRef.current);
      lastScrollTopRef.current = container.scrollTop;

      if (container.scrollTop >= TOP_THRESHOLD) {
        hasRequestedOlderRef.current = false;
      } else if (!hasRequestedOlderRef.current && !isLoadingOlderRef.current) {
        hasRequestedOlderRef.current = true;
        onReachTopRef.current?.();
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [hasMessages]);

  // Hiding the container destroys its layout box, taking the scroll offset with it.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const observer = new ResizeObserver(() => {
      if (container.clientHeight === 0) return;

      container.scrollTop = isPinnedRef.current
        ? container.scrollHeight
        : lastScrollTopRef.current;
    });

    observer.observe(container);
    return () => observer.disconnect();
  }, [hasMessages]);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const first = messages[0];
    const last = messages[messages.length - 1];
    const signature = `${messages.length}:${last?.id ?? ''}:${last?.parts.length ?? 0}:${measureContentLength(last)}`;
    if (signature === lastSignatureRef.current) {
      lastScrollHeightRef.current = container.scrollHeight;
      return;
    }
    lastSignatureRef.current = signature;

    const previousIds = lastIdsRef.current;
    const previousCount = lastCountRef.current;
    const previousScrollHeight = lastScrollHeightRef.current;

    const isNewMessage = messages.length > previousCount;
    lastCountRef.current = messages.length;
    lastIdsRef.current = { first: first?.id ?? '', last: last?.id ?? '' };
    lastScrollHeightRef.current = container.scrollHeight;

    const isPrepend =
      isNewMessage &&
      previousCount > 0 &&
      last?.id === previousIds.last &&
      first?.id !== previousIds.first;

    if (isPrepend) {
      container.scrollTop += container.scrollHeight - previousScrollHeight;
      hasRequestedOlderRef.current = false;
      return;
    }

    const isOwnNewMessage = isNewMessage && last?.role === 'user';

    if (isOwnNewMessage || isPinnedRef.current) {
      container.scrollTop = container.scrollHeight;
      isPinnedRef.current = true;
    }
  }, [messages]);

  useEffect(() => {
    if (!isLoadingOlder) hasRequestedOlderRef.current = false;
  }, [isLoadingOlder]);

  const scrollToLatest = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    container.scrollTop = container.scrollHeight;
    isPinnedRef.current = true;
    setIsScrolledUp(false);
  }, []);

  return { containerRef, isScrolledUp, scrollToLatest };
};
